'use client';

import * as React from 'react';

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Button,
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
  Checkbox,
  Input,
  Label,
  NumericInput,
  ScrollArea,
  Slider,
  Slot,
  Switch,
  VirtualList,
  XIcon,
} from '@/components';
import { useQueryParam } from '@/hooks';
import type { ProductsExtraResult } from '@/types';
import {
  addItemToValue,
  cn,
  flatMapTreeOption,
  hasIntersection,
  numberFormat,
  PATHNAME,
  randomId,
  removeItemFromValue,
  UNIT,
} from '@/utils';

const {
  QUERY_PARAM: {
    SYMBOL: { PIPELINE: PIPELINE_SYMBOL },
    SEARCH_KEY: { BASE_URL: SEARCH_KEY_BASE_URL },
    CATEGORIES_IDS: { BASE_URL: CATEGORIES_IDS_BASE_URL },
    BRANDS_IDS: { BASE_URL: BRANDS_IDS_BASE_URL },
    ATTRIBUTES_NAMES: { BASE_URL: ATTRIBUTES_NAMES_BASE_URL },
    ATTRIBUTES_VALUES: { BASE_URL: ATTRIBUTES_VALUES_BASE_URL },
    MIN_PRICE: { BASE_URL: MIN_PRICE_BASE_URL },
    MAX_PRICE: { BASE_URL: MAX_PRICE_BASE_URL },
    IS_AVAILABLE: { BASE_URL: IS_AVAILABLE_BASE_URL },
  },
} = PATHNAME;
const { PRICE } = UNIT;

const CategoryFiltersSearch = () => {
  const [queryParam, setQueryParam] = useQueryParam({
    initialValue: { [SEARCH_KEY_BASE_URL]: '' },
    routerOptions: { scroll: false },
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className='text-base'>جستجو در نتایج</CardTitle>
      </CardHeader>
      <CardContent>
        <Input
          type='search'
          placeholder='نام محصول و برند را وارد کنید'
          className='rounded border-transparent bg-secondary-foreground'
          defaultValue={queryParam[SEARCH_KEY_BASE_URL]}
          onChange={({ target: { value } }) => {
            setQueryParam({
              ...queryParam,
              [SEARCH_KEY_BASE_URL]: value,
            });
          }}
        />
      </CardContent>
    </Card>
  );
};

const CategoryFiltersAvailable = () => {
  const [queryParam, setQueryParam] = useQueryParam<
    Partial<Record<typeof IS_AVAILABLE_BASE_URL, boolean>>
  >({
    initialValue: { [IS_AVAILABLE_BASE_URL]: false },
    routerOptions: { scroll: false },
    delay: 250,
  });

  return (
    <Card>
      <CardContent className='flex-row items-center justify-between'>
        <Label
          htmlFor='is-available-filter'
          className='text-base font-semibold'
        >
          فقط کالاهای موجود
        </Label>
        <Switch
          id='is-available-filter'
          checked={queryParam[IS_AVAILABLE_BASE_URL]}
          onCheckedChange={checked => {
            setQueryParam({
              ...queryParam,
              [IS_AVAILABLE_BASE_URL]: checked,
            });
          }}
        />
      </CardContent>
    </Card>
  );
};

const CategoryFiltersCategories = ({
  availableCategories,
}: Pick<CategoryFiltersProps, 'availableCategories'>) => {
  const [queryParam, setQueryParam] = useQueryParam({
    initialValue: { [CATEGORIES_IDS_BASE_URL]: '' },
    routerOptions: { scroll: false },
  });

  const categoriesIds =
    queryParam[CATEGORIES_IDS_BASE_URL].split(PIPELINE_SYMBOL);

  const isActiveFilter = Boolean(queryParam[CATEGORIES_IDS_BASE_URL]);

  const flattenedResults = flatMapTreeOption(availableCategories);
  const selectedCategories = flattenedResults.filter(({ id }) =>
    categoriesIds.includes(id),
  );

  const renderCategoriesTree = (data: typeof availableCategories) =>
    data.map(({ id, title, children }) => {
      const isChecked = categoriesIds.includes(id);

      return children.length > 0 ? (
        <Accordion key={id} type='single' className='w-full' collapsible>
          <AccordionItem value={id} className='py-1'>
            <div className='flex justify-between'>
              <div className='flex items-center gap-2'>
                <Checkbox
                  id={id}
                  checked={isChecked}
                  onCheckedChange={checked => {
                    const prevCategoriesIds =
                      queryParam[CATEGORIES_IDS_BASE_URL];

                    setQueryParam({
                      ...queryParam,
                      [CATEGORIES_IDS_BASE_URL]: checked
                        ? addItemToValue(prevCategoriesIds, PIPELINE_SYMBOL, id)
                        : removeItemFromValue(
                            prevCategoriesIds,
                            PIPELINE_SYMBOL,
                            id,
                          ),
                    });
                  }}
                />
                <Label htmlFor={id} className='text-sm text-secondary'>
                  {title}
                </Label>
              </div>

              <AccordionTrigger className='[&>svg]:size-4' />
            </div>

            <AccordionContent className='ps-5 pt-1'>
              {renderCategoriesTree(children)}
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      ) : (
        <li key={id} className='my-1.5 flex items-center gap-2'>
          <Checkbox
            id={id}
            checked={isChecked}
            onCheckedChange={checked => {
              const prevCategoriesIds = queryParam[CATEGORIES_IDS_BASE_URL];

              setQueryParam({
                ...queryParam,
                [CATEGORIES_IDS_BASE_URL]: checked
                  ? addItemToValue(prevCategoriesIds, PIPELINE_SYMBOL, id)
                  : removeItemFromValue(prevCategoriesIds, PIPELINE_SYMBOL, id),
              });
            }}
          />
          <Label htmlFor={id} className='text-sm text-secondary'>
            {title}
          </Label>
        </li>
      );
    });

  return (
    availableCategories.length > 0 && (
      <Card className={cn(isActiveFilter && 'border-primary/30')}>
        <CardContent>
          <Accordion type='single' className='w-full' collapsible>
            <AccordionItem value='categories-ids'>
              <AccordionTrigger className='text-base'>
                دسته بندی
              </AccordionTrigger>

              <AccordionContent>
                <ScrollArea className='h-60 data-[radix-scroll-area-viewport]:*:pe-2.5'>
                  <ul>{renderCategoriesTree(availableCategories)}</ul>
                </ScrollArea>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </CardContent>
        {selectedCategories.length > 0 ? (
          <CardFooter className='flex-wrap gap-1'>
            {selectedCategories.map(({ id, title }) => (
              <Button
                key={id}
                variant='muted'
                className='h-auto gap-1 px-2 py-1 text-xs'
                onClick={() => {
                  const prevCategoriesIds = queryParam[CATEGORIES_IDS_BASE_URL];

                  setQueryParam({
                    ...queryParam,
                    [CATEGORIES_IDS_BASE_URL]: removeItemFromValue(
                      prevCategoriesIds,
                      PIPELINE_SYMBOL,
                      id,
                    ),
                  });
                }}
              >
                {title}
                <XIcon className='size-2' />
              </Button>
            ))}
          </CardFooter>
        ) : null}
      </Card>
    )
  );
};

const CategoryFiltersBrands = ({
  availableBrands,
}: Pick<CategoryFiltersProps, 'availableBrands'>) => {
  const [queryParam, setQueryParam] = useQueryParam({
    initialValue: { [BRANDS_IDS_BASE_URL]: '' },
    routerOptions: { scroll: false },
  });

  const brandsIds = queryParam[BRANDS_IDS_BASE_URL].split(PIPELINE_SYMBOL);

  const isActiveFilter = Boolean(queryParam[BRANDS_IDS_BASE_URL]);

  const selectedBrands = availableBrands.filter(({ id }) =>
    brandsIds.includes(id),
  );

  return (
    <Card className={cn(isActiveFilter && 'border-primary/30')}>
      <CardContent>
        <Accordion type='single' className='w-full' collapsible>
          <AccordionItem value='brands-ids'>
            <AccordionTrigger className='text-base'>برند</AccordionTrigger>

            <AccordionContent>
              <VirtualList<typeof availableBrands>
                width='100%'
                height={160}
                itemSize={30}
                itemData={availableBrands}
                itemCount={availableBrands.length}
              >
                {({ data, index, ...props }) => {
                  const { id, title } = data[index];
                  const isChecked = brandsIds.includes(id);

                  return (
                    <li key={id} className='flex items-center gap-2' {...props}>
                      <Checkbox
                        id={id}
                        checked={isChecked}
                        onCheckedChange={checked => {
                          const prevBrandsIds = queryParam[BRANDS_IDS_BASE_URL];

                          setQueryParam({
                            ...queryParam,
                            [BRANDS_IDS_BASE_URL]: checked
                              ? addItemToValue(
                                  prevBrandsIds,
                                  PIPELINE_SYMBOL,
                                  id,
                                )
                              : removeItemFromValue(
                                  prevBrandsIds,
                                  PIPELINE_SYMBOL,
                                  id,
                                ),
                          });
                        }}
                      />
                      <Label htmlFor={id} className='text-sm text-secondary'>
                        {title}
                      </Label>
                    </li>
                  );
                }}
              </VirtualList>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </CardContent>
      {selectedBrands.length > 0 ? (
        <CardFooter className='flex-wrap gap-1'>
          {selectedBrands.map(({ id, title }) => (
            <Button
              key={id}
              variant='muted'
              className='h-auto gap-1 px-2 py-1 text-xs'
              onClick={() => {
                const prevBrandsIds = queryParam[BRANDS_IDS_BASE_URL];

                setQueryParam({
                  ...queryParam,
                  [BRANDS_IDS_BASE_URL]: removeItemFromValue(
                    prevBrandsIds,
                    PIPELINE_SYMBOL,
                    id,
                  ),
                });
              }}
            >
              {title}
              <XIcon className='size-2' />
            </Button>
          ))}
        </CardFooter>
      ) : null}
    </Card>
  );
};

const CategoryFiltersPrices = ({
  availablePriceRange,
}: Pick<CategoryFiltersProps, 'availablePriceRange'>) => {
  const {
    minPrice: defaultMinPrice = 0,
    maxPrice: defaultMaxPrice = 0,
    priceStep: defaultPriceStep = 0,
  } = availablePriceRange ?? {};

  const [queryParam, setQueryParam] = useQueryParam<
    Partial<
      Record<
        typeof MIN_PRICE_BASE_URL | typeof MAX_PRICE_BASE_URL,
        string | number
      >
    >
  >({
    initialValue: {
      [MIN_PRICE_BASE_URL]: defaultMinPrice,
      [MAX_PRICE_BASE_URL]: defaultMaxPrice,
    },
    routerOptions: {
      scroll: false,
    },
  });
  const initialMinPrice = Number(queryParam[MIN_PRICE_BASE_URL]);
  const initialMaxPrice = Number(queryParam[MAX_PRICE_BASE_URL]);

  const [priceRange, setPriceRange] = React.useState(() => ({
    [MIN_PRICE_BASE_URL]: initialMinPrice,
    [MAX_PRICE_BASE_URL]: initialMaxPrice,
  }));
  const currentMinPrice = priceRange[MIN_PRICE_BASE_URL];
  const currentMaxPrice = priceRange[MAX_PRICE_BASE_URL];

  React.useEffect(() => {
    if (
      initialMinPrice !== currentMinPrice ||
      initialMaxPrice !== currentMaxPrice
    ) {
      onPriceRange({
        minPrice: initialMinPrice,
        maxPrice: initialMaxPrice,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialMinPrice, initialMaxPrice]);

  const onPriceRange = React.useCallback(
    ({
      minPrice = currentMinPrice,
      maxPrice = currentMaxPrice,
    }: Partial<NonNullable<typeof availablePriceRange>>) => {
      const formattedMinPrice = Math.max(
        defaultMinPrice,
        Math.min(minPrice, maxPrice),
      );
      const formattedMaxPrice = Math.min(
        defaultMaxPrice,
        Math.max(maxPrice, minPrice),
      );

      setPriceRange(prev => ({
        ...prev,
        [MIN_PRICE_BASE_URL]: formattedMinPrice,
        [MAX_PRICE_BASE_URL]: formattedMaxPrice,
      }));
      setQueryParam({
        ...queryParam,
        [MIN_PRICE_BASE_URL]: formattedMinPrice,
        [MAX_PRICE_BASE_URL]: formattedMaxPrice,
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      currentMinPrice,
      currentMaxPrice,
      defaultMinPrice,
      defaultMaxPrice,
      queryParam,
    ],
  );

  const isActiveFilter =
    currentMinPrice !== defaultMinPrice || currentMaxPrice !== defaultMaxPrice;

  return (
    <Card className={cn(isActiveFilter && 'border-primary/30')}>
      <CardContent>
        <Accordion type='single' className='w-full' collapsible>
          <AccordionItem value='prices'>
            <AccordionTrigger className='text-base'>
              محدوده قیمت
            </AccordionTrigger>
            <AccordionContent>
              <div className='my-8'>
                <Slider
                  value={[currentMinPrice, currentMaxPrice]}
                  min={defaultMinPrice}
                  max={defaultMaxPrice}
                  step={defaultPriceStep}
                  onValueChange={([minPrice, maxPrice]) => {
                    onPriceRange({
                      minPrice,
                      maxPrice,
                    });
                  }}
                />
              </div>

              <div className='flex items-center justify-between gap-5'>
                <span className='text-sm text-secondary'>از</span>
                <NumericInput
                  allowCombine
                  className='bg-muted text-center'
                  addonAfter={PRICE}
                  value={currentMinPrice}
                  onChange={({ target: { value } }) => {
                    onPriceRange({
                      minPrice: Number(value),
                    });
                  }}
                />
              </div>

              <div className='my-2 flex items-center justify-between gap-5'>
                <span className='text-sm text-secondary'>تا</span>
                <NumericInput
                  allowCombine
                  className='bg-muted text-center'
                  addonAfter={PRICE}
                  value={currentMaxPrice}
                  onChange={({ target: { value } }) => {
                    onPriceRange({
                      maxPrice: Number(value),
                    });
                  }}
                />
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </CardContent>
    </Card>
  );
};

const CategoryFiltersAttributes = ({
  availableAttributes,
}: Pick<CategoryFiltersProps, 'availableAttributes'>) => {
  const [queryParam, setQueryParam] = useQueryParam({
    initialValue: {
      [ATTRIBUTES_NAMES_BASE_URL]: '',
      [ATTRIBUTES_VALUES_BASE_URL]: '',
    },
    routerOptions: {
      scroll: false,
    },
  });

  const attributeNames =
    queryParam[ATTRIBUTES_NAMES_BASE_URL].split(PIPELINE_SYMBOL);
  const attributeValues =
    queryParam[ATTRIBUTES_VALUES_BASE_URL].split(PIPELINE_SYMBOL);

  return availableAttributes.length > 0
    ? availableAttributes.map(({ id, name, values }) => {
        const isActiveFilter = attributeNames.includes(name);

        const selectedValues = values.filter(value =>
          attributeValues.includes(value),
        );

        return (
          <Card key={id} className={cn(isActiveFilter && 'border-primary/30')}>
            <CardContent>
              <Accordion type='single' className='w-full' collapsible>
                <AccordionItem value={name}>
                  <AccordionTrigger className='text-base'>
                    {name}
                  </AccordionTrigger>

                  <AccordionContent>
                    <VirtualList<typeof values>
                      width='100%'
                      height={160}
                      itemSize={30}
                      itemData={values}
                      itemCount={values.length}
                    >
                      {({ data, index, ...props }) => {
                        const subId = randomId();
                        const value = data[index];
                        const isChecked =
                          attributeNames.includes(name) &&
                          attributeValues.includes(value);

                        return (
                          <li
                            key={subId}
                            className='flex items-center gap-2'
                            {...props}
                          >
                            <Checkbox
                              id={subId}
                              checked={isChecked}
                              onCheckedChange={checked => {
                                const prevAttributeNamesQuery =
                                  queryParam[ATTRIBUTES_NAMES_BASE_URL];
                                const prevAttributeValuesQuery =
                                  queryParam[ATTRIBUTES_VALUES_BASE_URL];

                                setQueryParam({
                                  ...queryParam,
                                  [ATTRIBUTES_NAMES_BASE_URL]: checked
                                    ? addItemToValue(
                                        prevAttributeNamesQuery,
                                        PIPELINE_SYMBOL,
                                        name,
                                      )
                                    : !hasIntersection(
                                          values.filter(item => item !== value),
                                          attributeValues,
                                        )
                                      ? removeItemFromValue(
                                          prevAttributeNamesQuery,
                                          PIPELINE_SYMBOL,
                                          name,
                                        )
                                      : prevAttributeNamesQuery,
                                  [ATTRIBUTES_VALUES_BASE_URL]: checked
                                    ? addItemToValue(
                                        prevAttributeValuesQuery,
                                        PIPELINE_SYMBOL,
                                        value,
                                      )
                                    : removeItemFromValue(
                                        prevAttributeValuesQuery,
                                        PIPELINE_SYMBOL,
                                        value,
                                      ),
                                });
                              }}
                            />
                            <Label
                              htmlFor={subId}
                              className='text-sm text-secondary'
                            >
                              {value}
                            </Label>
                          </li>
                        );
                      }}
                    </VirtualList>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </CardContent>
            {selectedValues.length > 0 ? (
              <CardFooter className='flex-wrap gap-1'>
                {selectedValues.map(value => (
                  <Button
                    key={id}
                    variant='muted'
                    className='h-auto gap-1 px-2 py-1 text-xs'
                    onClick={() => {
                      const prevAttributeNamesQuery =
                        queryParam[ATTRIBUTES_NAMES_BASE_URL];
                      const prevAttributeValuesQuery =
                        queryParam[ATTRIBUTES_VALUES_BASE_URL];

                      setQueryParam({
                        ...queryParam,
                        [ATTRIBUTES_NAMES_BASE_URL]: !hasIntersection(
                          values.filter(item => item !== value),
                          attributeValues,
                        )
                          ? removeItemFromValue(
                              prevAttributeNamesQuery,
                              PIPELINE_SYMBOL,
                              name,
                            )
                          : prevAttributeNamesQuery,
                        [ATTRIBUTES_VALUES_BASE_URL]: removeItemFromValue(
                          prevAttributeValuesQuery,
                          PIPELINE_SYMBOL,
                          value,
                        ),
                      });
                    }}
                  >
                    {value}
                    <XIcon className='size-2' />
                  </Button>
                ))}
              </CardFooter>
            ) : null}
          </Card>
        );
      })
    : null;
};

type CategoryFiltersProps = React.ComponentPropsWithoutRef<'div'> &
  Partial<Record<'asChild', boolean>> &
  ProductsExtraResult;

const CategoryFilters = ({
  asChild = false,
  availablePriceRange,
  availableCategories,
  availableBrands,
  availableAttributes,
  className,
  ...props
}: CategoryFiltersProps) => {
  const Comp = asChild ? Slot : 'div';

  return (
    <Comp className={cn('flex flex-col gap-4', className)} {...props}>
      <CategoryFiltersSearch />
      <CategoryFiltersAvailable />
      <CategoryFiltersCategories availableCategories={availableCategories} />
      <CategoryFiltersBrands availableBrands={availableBrands} />
      <CategoryFiltersPrices availablePriceRange={availablePriceRange} />
      <CategoryFiltersAttributes availableAttributes={availableAttributes} />
    </Comp>
  );
};

export { CategoryFilters };
